import type { CopyPricePreviewResponse } from '@/api';
import copyPricesEmptyState from '@/assets/copy-prices-empty-state.svg';
import { ResponsiveEmptyStateIllustration } from '@/components/responsive-empty-state-illustration';
import { connectColors } from '@/theme/colors';
import { Flex, Text } from '@lego/klik-ui';
import type { RefObject } from 'react';
import CopyPricePreviewTable from './copy-price-preview-table';

type CopyPricePreviewProps = {
  previewData: CopyPricePreviewResponse | null;
  isExecuting: boolean;
  triggerRef: RefObject<HTMLElement | null>;
  onConfirmCopy: () => void;
};
export const CopyPricePreview = ({
  previewData,
  isExecuting,
  triggerRef,
  onConfirmCopy,
}: CopyPricePreviewProps) => (
  <Flex
    padding="0 4px 4px"
    flexDir="column"
    flex="1"
    height="100%"
    gap="16px"
    overflow="hidden"
    minHeight={0}
  >
    {previewData ? (
      <Flex flexDir="column" gap="16px" height="100%">
        <Flex
          flexDir="column"
          padding="20px"
          gap="8px"
          background="var(--ds-color-interactive-primary-subtle-selected)"
        >
          <Flex gap="16px">
            {(
              [
                { label: 'Total', value: previewData.totalCount },
                { label: 'To copy', value: previewData.wouldCopyCount },
                { label: 'To skip', value: previewData.wouldSkipCount || '-' },
              ] as const
            ).map(({ label, value }) => (
              <Flex key={label} flexDir="column" gap="2px" minWidth="80px">
                <Text fontSize="12px" color={connectColors.core.offBlack}>
                  {label}
                </Text>
                <Flex
                  padding="4px 16px"
                  borderRadius="8px"
                  border={`1px solid ${connectColors.core.gray[200]}`}
                  backgroundColor={connectColors.core.white}
                  justifyContent="center"
                >
                  <Text fontSize="12px" fontWeight="bold">
                    {value}
                  </Text>
                </Flex>
              </Flex>
            ))}
          </Flex>
        </Flex>
        {previewData.errors.length > 0 && (
          <Flex flexDir="column" gap="4px">
            {previewData.errors.map((err) => (
              <Text key={err} color="red.500" fontSize="14px">
                {err}
              </Text>
            ))}
          </Flex>
        )}
        <CopyPricePreviewTable
          items={previewData.items}
          isExecuting={isExecuting}
          wouldCopyCount={previewData.wouldCopyCount}
          triggerRef={triggerRef}
          onConfirmCopy={onConfirmCopy}
        />
      </Flex>
    ) : (
      <ResponsiveEmptyStateIllustration
        src={copyPricesEmptyState}
        alt="Copy prices empty state"
        maxWidth={1492}
        alignment="left"
      />
    )}
  </Flex>
);
